import clsx from "clsx";
import { ArrowDown, ArrowUp, Eye, EyeOff, Plus, Trash2, X } from "lucide-react";
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { forwardRef, useEffect, useState } from "react";

import type { Localized } from "@/lib/types";

import { Button, IconButton } from "./core";

const hasWidth = (className?: string) => /(^|\s)w-/.test(className ?? "");
const control =
  "rounded border border-line-strong bg-surface px-2.5 text-sm text-ink placeholder:text-ink-3 hover:border-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:bg-subtle disabled:text-ink-3";

export function Field({ label, hint, error, required, children, className }: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={clsx("block min-w-0", className)}>
      {label && (
        <span className="mb-1 block text-xs font-medium text-ink-2">
          {label}
          {required && <span className="ml-0.5 text-bad">*</span>}
        </span>
      )}
      {children}
      {error ? <span className="mt-1 block text-xs text-bad">{error}</span> : hint ? <span className="mt-1 block text-xs text-ink-3">{hint}</span> : null}
    </label>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }>(function Input(
  { className, mono, ...rest },
  ref,
) {
  return <input ref={ref} className={clsx(control, !hasWidth(className) && "w-full", "h-8", mono && "font-mono text-xs", className)} {...rest} />;
});

/** Masked input for API keys and secrets, with a show/hide toggle so the pasted value can be checked. */
export const SecretInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { mono?: boolean }>(
  function SecretInput({ className, ...rest }, ref) {
    const [visible, setVisible] = useState(false);
    return (
      <div className={clsx("relative", !hasWidth(className) && "w-full", className)}>
        <Input ref={ref} type={visible ? "text" : "password"} autoComplete="off" spellCheck={false} className="w-full pr-9" {...rest} />
        <IconButton
          label={visible ? "Gizle" : "Göster"}
          icon={visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          className="absolute right-0.5 top-0.5"
          onClick={() => setVisible((v) => !v)}
        />
      </div>
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean }>(
  function Textarea({ className, mono, rows = 3, ...rest }, ref) {
    return <textarea ref={ref} rows={rows} className={clsx(control, "w-full py-1.5 leading-5", mono && "font-mono text-xs", className)} {...rest} />;
  },
);

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={clsx(control, !hasWidth(className) && "w-full", "h-8 pr-7", className)} {...rest}>
      {children}
    </select>
  );
}

export function Checkbox({ label, checked, onChange, disabled, hint }: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  return (
    <label className={clsx("flex cursor-pointer items-start gap-2 text-sm", disabled && "cursor-not-allowed opacity-60")}>
      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[rgb(var(--brand))]" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <span className="block text-xs text-ink-3">{hint}</span>}
      </span>
    </label>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 text-sm disabled:opacity-50"
    >
      <span className={clsx("relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors", checked ? "bg-brand" : "bg-line-strong")}>
        <span className={clsx("absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform", checked ? "translate-x-3.5" : "translate-x-0.5")} />
      </span>
      {label}
    </button>
  );
}

/** Turkish/English pair editor for localized texts. */
export function LocalizedInput({ value, onChange, multiline, placeholder, rows = 2 }: {
  value: Localized;
  onChange: (v: Localized) => void;
  multiline?: boolean;
  placeholder?: string;
  rows?: number;
}) {
  const set = (lang: string, text: string) => {
    const next = { ...value };
    if (text) next[lang] = text;
    else delete next[lang];
    onChange(next);
  };
  return (
    <div className="space-y-1.5">
      {(["tr", "en"] as const).map((lang) => (
        <div key={lang} className="flex items-start gap-1.5">
          <span className="mt-1.5 w-6 shrink-0 text-2xs font-semibold uppercase text-ink-3">{lang}</span>
          {multiline ? (
            <Textarea rows={rows} value={value[lang] ?? ""} placeholder={lang === "tr" ? placeholder : undefined} onChange={(e) => set(lang, e.target.value)} />
          ) : (
            <Input value={value[lang] ?? ""} placeholder={lang === "tr" ? placeholder : undefined} onChange={(e) => set(lang, e.target.value)} />
          )}
        </div>
      ))}
    </div>
  );
}

/** Free-text list editor (keywords, native ids, sample utterances...). */
export function TagInput({ value, onChange, placeholder, mono }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string; mono?: boolean }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const parts = draft.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (parts.length) onChange([...value, ...parts.filter((p) => !value.includes(p))]);
    setDraft("");
  };
  return (
    <div className={clsx(control, "flex min-h-[32px] w-full flex-wrap items-center gap-1 py-1")}>
      {value.map((tag) => (
        <span key={tag} className={clsx("inline-flex items-center gap-1 rounded bg-subtle px-1.5 py-0.5 text-xs", mono && "font-mono")}>
          {tag}
          <button type="button" className="text-ink-3 hover:text-bad" onClick={() => onChange(value.filter((t) => t !== tag))} aria-label={`${tag} kaldır`}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        className="min-w-[120px] flex-1 bg-transparent py-0.5 text-sm outline-none"
        value={draft}
        placeholder={value.length ? "" : placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
          } else if (e.key === "Backspace" && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={add}
      />
    </div>
  );
}

/**
 * Multi-select as toggle chips (small, known option sets: roles, levels, scopes). Selected values that
 * are not among the options stay visible (marked) so nothing is silently dropped.
 */
export function ChipSelect({ options, value, onChange, emptyText = "Seçenek yok", disabled }: {
  options: { value: string; label?: ReactNode; title?: string }[];
  value: string[];
  onChange: (v: string[]) => void;
  emptyText?: string;
  disabled?: boolean;
}) {
  const known = new Set(options.map((o) => o.value));
  const unknown = value.filter((v) => !known.has(v));
  if (!options.length && !unknown.length) return <div className="text-xs text-ink-3">{emptyText}</div>;
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            title={o.title}
            disabled={disabled}
            onClick={() => toggle(o.value)}
            className={clsx(
              "inline-flex h-6 items-center gap-1 rounded border px-2 text-xs transition-colors disabled:opacity-50",
              on ? "border-brand bg-brand-soft text-brand" : "border-line-strong bg-surface text-ink-2 hover:border-ink-3",
            )}
          >
            {o.label ?? o.value}
          </button>
        );
      })}
      {unknown.map((v) => (
        <button
          key={v}
          type="button"
          title="Tanımlı seçeneklerde yok — kaldırmak için tıklayın"
          disabled={disabled}
          onClick={() => toggle(v)}
          className="inline-flex h-6 items-center gap-1 rounded border border-warn/40 bg-warn-soft px-2 font-mono text-xs text-warn"
        >
          {v} <X className="h-3 w-3" />
        </button>
      ))}
    </div>
  );
}

/** One item per line — friendlier than tags for sentences (sample utterances, rules). */
export function LinesInput({ value, onChange, rows = 4, placeholder }: { value: string[]; onChange: (v: string[]) => void; rows?: number; placeholder?: string }) {
  const [text, setText] = useState(value.join("\n"));
  useEffect(() => {
    setText(value.join("\n"));
  }, [value]);
  return (
    <Textarea
      rows={rows}
      value={text}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(text.split("\n").map((s) => s.trim()).filter(Boolean))}
    />
  );
}

const LIST_MARKER = /^\s*(?:\d+[.)]|[-*•])\s+/;

/**
 * Ordered sentences (e.g. a screen's how-to steps): numbered rows that can be edited, moved and removed.
 * Pasting several lines into a row turns each line into its own step (leading "1." / "-" markers dropped).
 */
export function OrderedLinesInput({ value, onChange, placeholder, addLabel = "Adım ekle", emptyText = "Henüz adım yok" }: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  addLabel?: string;
  emptyText?: string;
}) {
  const [focusLast, setFocusLast] = useState(false);
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <div className="rounded border border-dashed border-line px-3 py-4 text-center text-xs text-ink-3">{emptyText}</div>
      ) : (
        <ol className="space-y-1.5">
          {value.map((step, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1.5 w-6 shrink-0 text-right text-xs tabular-nums text-ink-3">{i + 1}.</span>
              <Textarea
                rows={Math.min(4, Math.max(1, Math.ceil(step.length / 90)))}
                value={step}
                placeholder={placeholder}
                autoFocus={focusLast && i === value.length - 1}
                className="min-h-[32px]"
                onChange={(e) => onChange(value.map((v, j) => (j === i ? e.target.value : v)))}
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text");
                  if (!/[\r\n]/.test(text)) return;
                  e.preventDefault();
                  const lines = text.split(/\r?\n/).map((l) => l.replace(LIST_MARKER, "").trim()).filter(Boolean);
                  const next = [...value];
                  next.splice(i, 1, ...(step.trim() ? [step, ...lines] : lines));
                  onChange(next);
                }}
              />
              <div className="flex shrink-0 items-center pt-0.5">
                <IconButton label="Yukarı" icon={<ArrowUp className="h-3.5 w-3.5" />} onClick={() => move(i, -1)} disabled={i === 0} />
                <IconButton label="Aşağı" icon={<ArrowDown className="h-3.5 w-3.5" />} onClick={() => move(i, 1)} disabled={i === value.length - 1} />
                <IconButton label="Sil" icon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => onChange(value.filter((_, j) => j !== i))} />
              </div>
            </li>
          ))}
        </ol>
      )}
      <Button
        size="sm"
        icon={<Plus className="h-3.5 w-3.5" />}
        onClick={() => {
          setFocusLast(true);
          onChange([...value, ""]);
        }}
      >
        {addLabel}
      </Button>
    </div>
  );
}

/** JSON editor with live validation; calls onChange only with valid JSON. */
export function JsonEditor({ value, onChange, rows = 10, readOnly }: { value: unknown; onChange?: (v: unknown) => void; rows?: number; readOnly?: boolean }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setText(JSON.stringify(value ?? {}, null, 2));
    setError(null);
  }, [value]);
  return (
    <div>
      <Textarea
        mono
        rows={rows}
        spellCheck={false}
        readOnly={readOnly}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value);
            setError(null);
            onChange?.(parsed);
          } catch (err) {
            setError((err as Error).message);
          }
        }}
        className={clsx(error && "border-bad focus:border-bad focus:ring-bad")}
      />
      {error && <div className="mt-1 text-xs text-bad">Geçersiz JSON: {error}</div>}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Ara…", className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return <Input type="search" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={clsx("w-64", className)} />;
}
